import { jsx as _jsx } from "react/jsx-runtime";
/**
 * UserContext
 *
 * Exposes the authenticated user's profile (userId / UUID, firstName,
 * lastName) for the rest of the application.
 *
 * Source of truth
 * ---------------
 * The profile is derived directly from the JWT access token returned by
 * `POST /api/auth/token` (and refreshed on `POST /api/auth/refresh`). The
 * legacy `GET /api/getUserInfo` round-trip has been removed — the backend
 * embeds `email`, `first_name`, and `last_name` as JWT claims and surfaces
 * them in the token response so the client can hydrate state in one call.
 *
 * Lifecycle
 * ---------
 * 1. AuthContext exchanges the MSAL token and caches the User profile inside
 *    `tokenService`.
 * 2. This provider mirrors that profile into React state (camelCase shape
 *    expected by existing components) and into sessionStorage, so
 *    `axiosInstance` can synchronously attach `X-User-Id` to every request.
 * 3. `accessDenied` is true when the auth flow completed but the backend
 *    rejected the user (active=false or 403/404).
 * 4. On logout/unauthenticated, all caches are cleared.
 */
import { createContext, useContext, useState, useEffect, } from 'react';
import { useAuth } from '../auth/AuthContext';
// ─── Context ──────────────────────────────────────────────────────────────────
const UserContext = createContext({
    userInfo: null,
    isLoading: false,
    accessDenied: false,
    error: null,
});
/** Hook — consume anywhere inside <UserProvider>. */
export const useUser = () => useContext(UserContext);
/** sessionStorage key used by axiosInstance to read the UUID. */
export const USER_SESSION_KEY = 'userInfo';
/** sessionStorage key set to 'true' when the user is not authorised. */
export const ACCESS_DENIED_SESSION_KEY = 'accessDenied';
// ─── Provider ─────────────────────────────────────────────────────────────────
export const UserProvider = ({ children, }) => {
    const { user, isAuthenticated, isLoading: authLoading, error: authError } = useAuth();
    // Hydrate from sessionStorage so that on a hard refresh we have something
    // for axiosInstance to read while the auth flow is still resolving.
    const [userInfo, setUserInfo] = useState(() => {
        try {
            const stored = sessionStorage.getItem(USER_SESSION_KEY);
            if (!stored)
                return null;
            const parsed = JSON.parse(stored);
            return (parsed === null || parsed === void 0 ? void 0 : parsed.userId) ? parsed : null;
        }
        catch (_a) {
            return null;
        }
    });
    const [accessDenied, setAccessDenied] = useState(false);
    const [error, setError] = useState(null);
    // Loading while auth is resolving and we don't yet have a profile.
    const isLoading = authLoading && !userInfo;
    useEffect(() => {
        // Auth still resolving — don't make a decision yet.
        if (authLoading)
            return;
        // Unauthenticated → clear everything.
        if (!isAuthenticated) {
            sessionStorage.removeItem(USER_SESSION_KEY);
            sessionStorage.removeItem(ACCESS_DENIED_SESSION_KEY);
            sessionStorage.removeItem('accessDeniedEmail');
            setUserInfo(null);
            setAccessDenied(false);
            setError(null);
            return;
        }
        // Authenticated but backend rejected the user → access denied.
        if (!user) {
            const message = authError !== null && authError !== void 0 ? authError : 'Access denied';
            setError(message);
            setAccessDenied(true);
            sessionStorage.removeItem(USER_SESSION_KEY);
            sessionStorage.setItem(ACCESS_DENIED_SESSION_KEY, 'true');
            console.error('[UserContext] No user profile after auth:', message);
            return;
        }
        if (!user.active) {
            setError('User account is inactive');
            setAccessDenied(true);
            sessionStorage.removeItem(USER_SESSION_KEY);
            sessionStorage.setItem(ACCESS_DENIED_SESSION_KEY, 'true');
            sessionStorage.setItem('accessDeniedEmail', user.email);
            return;
        }
        // Happy path — map AuthContext User (snake_case) → UserInfo (camelCase).
        const info = {
            userId: user.user_id,
            firstName: user.first_name,
            lastName: user.last_name,
            email: user.email,
        };
        setUserInfo(info);
        setAccessDenied(false);
        setError(null);
        sessionStorage.removeItem(ACCESS_DENIED_SESSION_KEY);
        sessionStorage.removeItem('accessDeniedEmail');
        // Persist so axiosInstance can read it synchronously on every request.
        sessionStorage.setItem(USER_SESSION_KEY, JSON.stringify(info));
    }, [user, isAuthenticated, authLoading, authError]);
    return (_jsx(UserContext.Provider, { value: { userInfo, isLoading, accessDenied, error }, children: children }));
};
